import type { UserDirectoryEntry } from "./types";

export type UserTokenResolution = { readonly id: string } | { readonly error: string };

function same(value: string | null | undefined, query: string): boolean {
    return (value ?? "").trim().toLowerCase() === query;
}

/**
 * Turns a CSV / search token into an HR user id.
 *
 * Exact id, email, user principal name, or display name wins. A token
 * the directory does not recognize is left unchanged so an existing
 * import that already carries a user id still posts that id. A token
 * that returns people but matches none of them exactly is an error —
 * assigning "Ann" to the wrong Ann is worse than blocking the row.
 */
export function resolveUserAssignmentToken(
    token: string,
    results: readonly UserDirectoryEntry[],
): UserTokenResolution {
    const query = token.trim().toLowerCase();
    const byId = results.find((user) => user.id.trim().toLowerCase() === query);
    if (byId !== undefined) return { id: byId.id };

    const exact = results.filter(
        (user) =>
            same(user.email, query) ||
            same(user.userPrincipalName, query) ||
            same(user.displayName, query),
    );
    if (exact.length === 1) {
        const match = exact[0];
        if (match !== undefined) return { id: match.id };
    }
    if (exact.length > 1) {
        return {
            error: `'${token}' matches more than one user. Use an email or user id.`,
        };
    }
    if (results.length === 0) return { id: token };
    return {
        error: `No exact match for '${token}'. Use the full name, email, or user id.`,
    };
}
